import React, { useState } from 'react';
import { Row, Col, Form, Button, Dropdown } from 'react-bootstrap';

function TeacherFields({ formData, updateField }) {
  const filterOptions = (options, searchTerm) => {
    if (!searchTerm) return options;
    return options.filter(option =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Fixed: Single Select Dropdown Component
  const CustomDropdown = ({
    title,
    field,
    options,
    selectedValue,
    onSelect,
    required = false,
    menuClassName = ''
  }) => {
    const [localSearchTerm, setLocalSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const filteredOptions = filterOptions(options, localSearchTerm);

    return (
      <div>
        <Dropdown
          className="mb-3"
          show={isOpen}
          onToggle={(isOpen) => setIsOpen(isOpen)}
        >
          <Dropdown.Toggle
            variant="light"
            className="language-select w-100 d-flex justify-content-between align-items-center"
            id={`dropdown-${field}`}
          >
            {/* Fixed: Show selected value or placeholder with required indicator */}
            <span style={{ color: selectedValue ? '#212529' : '#6c757d' }}>
              {selectedValue ? selectedValue : `${title}${required ? ' *' : ''}`}
            </span>
          </Dropdown.Toggle>
          <Dropdown.Menu className={`w-100 ${menuClassName}`} style={{ overflowY: 'hidden' }}>
            <div className="px-2 py-1">
              <Form.Control
                type="text"
                placeholder={`Search ${title.toLowerCase()}...`}
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="mb-2"
                autoFocus
              />
            </div>
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <Dropdown.Item
                    key={option}
                    active={selectedValue === option}
                    onClick={() => {
                      onSelect(field, option);
                      setLocalSearchTerm('');
                      setIsOpen(false);
                    }}
                  >
                    {option}
                  </Dropdown.Item>
                ))
              ) : (
                <Dropdown.Item disabled>No matches found</Dropdown.Item>
              )}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  // Fixed: Multi-Select Dropdown Component
  const MultiSelectCustomDropdown = ({
    title,
    field,
    options,
    selectedValues = [],
    onSelect,
    required = false,
    menuClassName = ''
  }) => {
    const [localSearchTerm, setLocalSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const filteredOptions = filterOptions(options, localSearchTerm);

    const handleSelect = (option) => {
      onSelect(option);
      setLocalSearchTerm('');
    };

    return (
      <div>
        <Dropdown
          className="mb-3"
          show={isOpen}
          onToggle={(isOpen) => setIsOpen(isOpen)}
        >
          <Dropdown.Toggle
            variant="light"
            className="language-select w-100 d-flex justify-content-between align-items-center"
            id={`dropdown-${field}`}
          >
            {/* Fixed: Show selected count or placeholder */}
            <span style={{ color: selectedValues.length > 0 ? '#212529' : '#6c757d' }}>
              {selectedValues.length > 0
                ? `${title} (${selectedValues.length} selected)`
                : `${title}${required ? ' *' : ''}`
              }
            </span>
          </Dropdown.Toggle>
          <Dropdown.Menu className={`w-100 ${menuClassName}`} style={{ overflowY: 'hidden' }}>
            <div className="px-2 py-1">
              <Form.Control
                type="text"
                placeholder={`Search ${title.toLowerCase()}...`}
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="mb-2"
                autoFocus
              />
            </div>
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <Dropdown.Item
                    key={option}
                    active={selectedValues.includes(option)}
                    onClick={() => handleSelect(option)}
                  >
                    <div className="d-flex align-items-center">
                      {/* <Form.Check
                        type="checkbox"
                        checked={selectedValues.includes(option)}
                        onChange={() => {}}
                        className="me-2"
                      /> */}
                      {option}
                    </div>
                  </Dropdown.Item>
                ))
              ) : (
                <Dropdown.Item disabled>No matches found</Dropdown.Item>
              )}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  // Helper functions for multi-select fields (unchanged)
  const toggleTeachingMode = (mode) => {
    const updated = formData.teaching_mode?.includes(mode)
      ? formData.teaching_mode.filter(item => item !== mode)
      : [...(formData.teaching_mode || []), mode];
    updateField('teaching_mode', updated);
  };

  const toggleSubjects = (subject) => {
    const updated = formData.subjects?.includes(subject)
      ? formData.subjects.filter(item => item !== subject)
      : [...(formData.subjects || []), subject];
    updateField('subjects', updated);
  };

  const toggleAgeGroups = (ageGroup) => {
    const updated = formData.age_groups?.includes(ageGroup)
      ? formData.age_groups.filter(item => item !== ageGroup)
      : [...(formData.age_groups || []), ageGroup];
    updateField('age_groups', updated);
  };

  const toggleClasses = (classLevel) => {
    const updated = formData.classes?.includes(classLevel)
      ? formData.classes.filter(item => item !== classLevel)
      : [...(formData.classes || []), classLevel];
    updateField('classes', updated);
  };

  const toggleDailyHours = (hours) => {
    const updated = formData.daily_hours?.includes(hours)
      ? formData.daily_hours.filter(item => item !== hours)
      : [...(formData.daily_hours || []), hours];
    updateField('daily_hours', updated);
  };

  const toggleCurriculum = (curriculum) => {
    const updated = formData.curriculum?.includes(curriculum)
      ? formData.curriculum.filter(item => item !== curriculum)
      : [...(formData.curriculum || []), curriculum];
    updateField('curriculum', updated);
  };

  const toggleSpecialSkills = (skill) => {
    const updated = formData.special_skills?.includes(skill)
      ? formData.special_skills.filter(item => item !== skill)
      : [...(formData.special_skills || []), skill];
    updateField('special_skills', updated);
  };

  const toggleSportsGames = (sport) => {
    const updated = formData.sports_games?.includes(sport)
      ? formData.sports_games.filter(item => item !== sport)
      : [...(formData.sports_games || []), sport];
    updateField('sports_games', updated);
  };

  const toggleSportsAgeGroups = (ageGroup) => {
    const updated = formData.sports_age_groups?.includes(ageGroup)
      ? formData.sports_age_groups.filter(item => item !== ageGroup)
      : [...(formData.sports_age_groups || []), ageGroup];
    updateField('sports_age_groups', updated);
  };

  const toggleTrainingMode = (mode) => {
    const updated = formData.training_mode?.includes(mode)
      ? formData.training_mode.filter(item => item !== mode)
      : [...(formData.training_mode || []), mode];
    updateField('training_mode', updated);
  };

  const toggleOtherSkills = (skill) => {
    const updated = formData.teacher_other_skills?.includes(skill)
      ? formData.teacher_other_skills.filter(item => item !== skill)
      : [...(formData.teacher_other_skills || []), skill];
    updateField('teacher_other_skills', updated);
  };

  // Options arrays (unchanged)
  const teachingModesMain = ["For Home", "For School", "For College"];
  const teachingModesAdditional = [
    "Online", "Offline", "Hybrid", "Home Tuition",
    "Group Tuition", "Individual Tuition", "Coaching Center",
    "Institute", "Virtual Class", "Weekend Classes"
  ];

  const subjectsMain = ["English", "Arabic", "Mathematics"];
  const subjectsAdditional = [
    "Science", "Physics", "Chemistry", "Biology",
    "Computer Science", "Information Technology", "Economics",
    "Business Studies", "Accounting", "Geography", "History",
    "Environmental Science", "Social Studies", "Global Studies",
    "Moral Education", "Psychology", "Philosophy", "General Knowledge",
    "Hindi", "Urdu", "French", "Tamil", "Telugu", "Malayalam"
  ];

  const ageGroups = [
    "Below 3 Years", "3–5 Years", "6–10 Years",
    "11–14 Years", "15–18 Years", "19–22 Years",
    "23–25 Years", "26–30 Years", "31–35 Years"
  ];

  const classLevels = [
    "Playgroup", "Nursery", "LKG", "UKG", "KG",
    "Grade 1", "Grade 2", "Grade 3", "Grade 4",
    "Grade 5", "Grade 6", "Grade 7", "Grade 8",
    "Grade 9", "Grade 10", "Grade 11", "Grade 12",
    "Diploma", "Graduation", "Postgraduation",
    "Doctorate (PhD)", "Postdoctorate"
  ];

  const dailyHours = [
    "1–2 Hours", "2–4 Hours", "4–6 Hours",
    "6–8 Hours", "8–10 Hours", "10–12 Hours"
  ];

  const curriculumMain = ["IGCSE", "CBSE", "American"];
  const curriculumAdditional = [
    "Cambridge", "Edexcel", "NIOS", "ISC", "Matriculation",
    "SSC", "HSC", "ICAI", "NIIT", "Montessori", "Waldorf",
    "IGC", "CISCE"
  ];

  const specialSkillsMain = ["Phonics", "Spoken English", "Special Needs Support"];
  const specialSkillsAdditional = [
    "Handwriting Improvement", "Quran / Islamic Studies",
    "Homework Assistance", "Exam Preparation"
  ];

  const sportsGamesMain = ["Football", "Yoga", "Fitness / Zumba"];
  const sportsGamesAdditional = [
    "Cricket", "Badminton", "Basketball", "Volleyball",
    "Table Tennis", "Tennis", "Swimming", "Martial Arts (Karate, Taekwondo, Judo)",
    "Gymnastics", "Athletics", "Skating", "Cycling",
    "Handball", "Softball/Baseball", "Dance (Hip-hop, Contemporary, Classical)",
    "other Indoor Games"
  ];

  const sportsAgeGroups = ["3–5", "6–10", "11–14", "15–18"];

  const trainingModesMain = ["Football", "Yoga", "Fitness / Zumba"];
  const trainingModesAdditional = [
    "Home (Indoor)", "Outdoor (Park/Ground)", "Online (Yoga/Fitness)",
    "Gym/Fitness Center", "School Playground", "Sports Academy",
    "Virtual Classes", "Community Center", "Private Studio", "Weekend Camps"
  ];

  const otherSkillsMain = ["Cooking", "Gardening", "Child Care"];
  const otherSkillsAdditional = [
    "Cleaning", "Washing", "Laundry", "Mopping",
    "Sweeping", "Childcare", "Dishwashing",
    "Organizing", "Shopping", "Ironing", "Dusting", "Babysitting"
  ];

  // Render function
  return (
    <>
      {/* Teaching Mode - Fixed */}
      <div className="mb-5">
        <h4 className="mb-3" style={{ color: "black" }}>Teaching Mode *</h4>
        <Row className="g-3">
          {teachingModesMain.map((mode, idx) => (
            <Col xs={6} sm={6} md={4} key={idx}>
              <Button
                variant="light"
                className={`language-btn jobtype-btn w-100 ${formData.teaching_mode?.includes(mode) ? 'selected-button' : ''}`}
                onClick={() => toggleTeachingMode(mode)}
              >
                {mode}
              </Button>
            </Col>
          ))}
        </Row>

        <div className="mt-3">
          <MultiSelectCustomDropdown
            title="Select other teaching mode"
            field="teaching_mode_additional"
            options={teachingModesAdditional}
            selectedValues={formData.teaching_mode?.filter(mode =>
              teachingModesAdditional.includes(mode)
            ) || []}
            onSelect={toggleTeachingMode}
            menuClassName="personalinfo-nationality-menu"
          />
        </div>

        {/* Display selected teaching modes */}
        {formData.teaching_mode && formData.teaching_mode.length > 0 && (
          <div className="mt-3">
            <h6 className="mb-2">Selected Teaching Modes:</h6>
            <div className="d-flex flex-wrap gap-2">
              {formData.teaching_mode.map((mode, idx) => (
                <div key={idx} className="badge bg-primary d-flex align-items-center">
                  {mode}
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-2"
                    style={{ fontSize: '0.5rem' }}
                    onClick={() => toggleTeachingMode(mode)}
                    aria-label="Remove"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Subjects You Can Teach - Fixed */}
      <div className="mb-5">
        <h4 className="mb-3" style={{ color: "black" }}>Subjects You Can Teach *</h4>
        <Row className="g-3">
          {subjectsMain.map((subject, idx) => (
            <Col xs={6} sm={6} md={4} key={idx}>
              <Button
                variant="light"
                className={`language-btn jobtype-btn w-100 ${formData.subjects?.includes(subject) ? 'selected-button' : ''}`}
                onClick={() => toggleSubjects(subject)}
              >
                {subject}
              </Button>
            </Col>
          ))}
        </Row>

        <div className="mt-3">
          <MultiSelectCustomDropdown
            title="Select additional subject"
            field="subjects_additional"
            options={subjectsAdditional}
            selectedValues={formData.subjects?.filter(subject =>
              subjectsAdditional.includes(subject)
            ) || []}
            onSelect={toggleSubjects}
            menuClassName="personalinfo-nationality-menu"
          />
        </div>

        {/* Display selected subjects */}
        {formData.subjects && formData.subjects.length > 0 && (
          <div className="mt-3">
            <h6 className="mb-2">Selected Subjects:</h6>
            <div className="d-flex flex-wrap gap-2">
              {formData.subjects.map((subject, idx) => (
                <div key={idx} className="badge bg-primary d-flex align-items-center">
                  {subject}
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-2"
                    style={{ fontSize: '0.5rem' }}
                    onClick={() => toggleSubjects(subject)}
                    aria-label="Remove"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Age group of children taught - Fixed */}
      <div className="mb-5">
        <h4 className="mb-3" style={{ color: "black" }}>Age group of children taught *</h4>
        <MultiSelectCustomDropdown
          title="Select age group"
          field="age_groups"
          options={ageGroups}
          selectedValues={formData.age_groups || []}
          onSelect={toggleAgeGroups}
          menuClassName="personalinfo-nationality-menu"
        />

        {/* Display selected age groups */}
        {formData.age_groups && formData.age_groups.length > 0 && (
          <div className="mt-3">
            <h6 className="mb-2">Selected Age Groups:</h6>
            <div className="d-flex flex-wrap gap-2">
              {formData.age_groups.map((ageGroup, idx) => (
                <div key={idx} className="badge bg-primary d-flex align-items-center">
                  {ageGroup}
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-2"
                    style={{ fontSize: '0.5rem' }}
                    onClick={() => toggleAgeGroups(ageGroup)}
                    aria-label="Remove"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Which class do you teach? - Fixed */}
      <div className="mb-5">
        <h4 className="mb-3" style={{ color: "black" }}>Which class do you teach? *</h4>
        <MultiSelectCustomDropdown
          title="Select class level"
          field="classes"
          options={classLevels}
          selectedValues={formData.classes || []}
          onSelect={toggleClasses}
          menuClassName="personalinfo-nationality-menu"
        />

        {/* Display selected classes */}
        {formData.classes && formData.classes.length > 0 && (
          <div className="mt-3">
            <h6 className="mb-2">Selected Classes:</h6>
            <div className="d-flex flex-wrap gap-2">
              {formData.classes.map((classLevel, idx) => (
                <div key={idx} className="badge bg-primary d-flex align-items-center">
                  {classLevel}
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-2"
                    style={{ fontSize: '0.5rem' }}
                    onClick={() => toggleClasses(classLevel)}
                    aria-label="Remove"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Daily Teaching Hours - Fixed */}
      {/* Daily Teaching Hours - Fixed */}
      <div className="mb-5">
        <h4 className="mb-3" style={{ color: "black" }}>Daily Teaching Hours</h4>
        <Row className="g-3">
          {dailyHours.map((hours, idx) => (
            <Col xs={6} sm={4} md={3} key={idx}>
              <Button
                variant="light"
                className={`language-btn jobtype-btn w-100 ${formData.daily_hours === hours ? 'selected-button' : ''}`}
                onClick={() => updateField('daily_hours', hours)}
              >
                {hours}
              </Button>
            </Col>
          ))}
        </Row>

        {/* Display selected daily hours */}
        {formData.daily_hours && (
          <div className="mt-3">
            <h6 className="mb-2">Selected Daily Hours:</h6>
            <div className="d-flex flex-wrap gap-2">
              <div className="badge bg-primary d-flex align-items-center">
                {formData.daily_hours}
                <button
                  type="button"
                  className="btn-close btn-close-white ms-2"
                  style={{ fontSize: '0.5rem' }}
                  onClick={() => updateField('daily_hours', '')}
                  aria-label="Remove"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Curriculum / Board - Fixed */}
      <div className="mb-5">
        <h4 className="mb-3" style={{ color: "black" }}>Curriculum / Board *</h4>
        <Row className="g-3 mb-3">
          {curriculumMain.map((curriculum, idx) => (
            <Col xs={6} sm={6} md={4} key={idx}>
              <Button
                variant="light"
                className={`language-btn jobtype-btn w-100 ${formData.curriculum?.includes(curriculum) ? 'selected-button' : ''}`}
                onClick={() => toggleCurriculum(curriculum)}
              >
                {curriculum}
              </Button>
            </Col>
          ))}
        </Row>

        <div className="mt-3">
          <MultiSelectCustomDropdown
            title="Select other curriculum/board"
            field="curriculum_additional"
            options={curriculumAdditional}
            selectedValues={formData.curriculum?.filter(curriculum =>
              curriculumAdditional.includes(curriculum)
            ) || []}
            onSelect={toggleCurriculum}
            menuClassName="personalinfo-nationality-menu"
          />
        </div>

        {/* Display selected curriculum */}
        {formData.curriculum && formData.curriculum.length > 0 && (
          <div className="mt-3">
            <h6 className="mb-2">Selected Curriculum/Board:</h6>
            <div className="d-flex flex-wrap gap-2">
              {formData.curriculum.map((curriculum, idx) => (
                <div key={idx} className="badge bg-primary d-flex align-items-center">
                  {curriculum}
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-2"
                    style={{ fontSize: '0.5rem' }}
                    onClick={() => toggleCurriculum(curriculum)}
                    aria-label="Remove"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Special Teacher Skill - Fixed */}
      <div className="mb-5">
        <h4 className="mb-3" style={{ color: "black" }}>Special Teacher Skill</h4>
        <Row className="g-3 mb-3">
          {specialSkillsMain.map((skill, idx) => (
            <Col xs={6} sm={6} md={4} key={idx}>
              <Button
                variant="light"
                className={`language-btn jobtype-btn w-100 ${formData.special_skills?.includes(skill) ? 'selected-button' : ''}`}
                onClick={() => toggleSpecialSkills(skill)}
              >
                {skill}
              </Button>
            </Col>
          ))}
        </Row>

        <div className="mt-3">
          <MultiSelectCustomDropdown
            title="Select additional special skill"
            field="special_skills_additional"
            options={specialSkillsAdditional}
            selectedValues={formData.special_skills?.filter(skill =>
              specialSkillsAdditional.includes(skill)
            ) || []}
            onSelect={toggleSpecialSkills}
            menuClassName="personalinfo-nationality-menu"
          />
        </div>
      </div>

      {/* Sports & Games You Can Teach - Fixed */}
      <div className="mb-5">
        <h4 className="mb-3" style={{ color: "black" }}>Sports & Games You Can Teach</h4>
        <Row className="g-3 mb-3">
          {sportsGamesMain.map((sport, idx) => (
            <Col xs={6} sm={6} md={4} key={idx}>
              <Button
                variant="light"
                className={`language-btn jobtype-btn w-100 ${formData.sports_games?.includes(sport) ? 'selected-button' : ''}`}
                onClick={() => toggleSportsGames(sport)}
              >
                {sport}
              </Button>
            </Col>
          ))}
        </Row>

        <div className="mt-3">
          <MultiSelectCustomDropdown
            title="Select additional sports/games"
            field="sports_games_additional"
            options={sportsGamesAdditional}
            selectedValues={formData.sports_games?.filter(sport =>
              sportsGamesAdditional.includes(sport)
            ) || []}
            onSelect={toggleSportsGames}
            menuClassName="personalinfo-nationality-menu"
          />
        </div>
      </div>

      {/* Age Group for Sports - Fixed */}
      <div className="mb-5">
        <h4 className="mb-3" style={{ color: "black" }}>Age Group for Sports</h4>
        <Row className="g-3">
          {sportsAgeGroups.map((ageGroup, idx) => (
            <Col xs={6} sm={4} md={3} key={idx}>
              <Button
                variant="light"
                className={`language-btn jobtype-btn w-100 ${formData.sports_age_groups?.includes(ageGroup) ? 'selected-button' : ''}`}
                onClick={() => toggleSportsAgeGroups(ageGroup)}
              >
                {ageGroup}
              </Button>
            </Col>
          ))}
        </Row>
      </div>

      {/* Training Mode - Fixed */}
      <div className="mb-5">
        <h4 className="mb-3" style={{ color: "black" }}>Training Mode</h4>
        <Row className="g-3 mb-3">
          {trainingModesMain.map((mode, idx) => (
            <Col xs={6} sm={6} md={4} key={idx}>
              <Button
                variant="light"
                className={`language-btn jobtype-btn w-100 ${formData.training_mode?.includes(mode) ? 'selected-button' : ''}`}
                onClick={() => toggleTrainingMode(mode)}
              >
                {mode}
              </Button>
            </Col>
          ))}
        </Row>

        <div className="mt-3">
          <MultiSelectCustomDropdown
            title="Select other training mode"
            field="training_mode_additional"
            options={trainingModesAdditional}
            selectedValues={formData.training_mode?.filter(mode =>
              trainingModesAdditional.includes(mode)
            ) || []}
            onSelect={toggleTrainingMode}
            menuClassName="personalinfo-nationality-menu"
          />
        </div>
      </div>
    </>
  );
}

export default TeacherFields;